<template>
  <div class="max-w-6xl p-6 mx-auto md:p-8">
    <!-- Page header -->
    <div>
      <h2 class="text-2xl font-semibold text-gray-800">Reports</h2>
      <p class="text-sm text-gray-500">
        Filter the data you need and export it as a batch report
      </p>
    </div>

    <div class="mt-6 space-y-4">
      <!-- Filters card -->
      <section class="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm">
        <div class="flex items-center mb-4 space-x-1 text-gray-500">
          <span class="text-base material-icons-round">filter_list</span>
          <span class="text-xs font-semibold tracking-widest uppercase">Filters</span>
        </div>

        <div class="flex flex-wrap items-end gap-4">
          <!-- Driver -->
          <div class="relative w-full sm:w-56">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Driver</label>
            <div class="relative mt-1.5">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span class="text-base text-gray-500 material-icons-round">search</span>
              </span>
              <input
                v-model="selectedDriver"
                @keyup="searchDriver"
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Search drivers..."
              />
            </div>
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div
                v-if="driverSearchResults.length"
                class="absolute inset-x-0 z-50 mt-2"
              >
                <div
                  class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64"
                >
                  <ul>
                    <li
                      v-for="(result, index) in driverSearchResults"
                      :key="result._id"
                      @click="selectDriver(index)"
                      class="flex items-center px-3 py-2 space-x-2 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                    >
                      <img
                        class="object-cover w-10 h-10 rounded-full"
                        :src="result.profileImage"
                        :alt="`${result.firstName} ${result.lastName}`"
                      />
                      <div>
                        <p class="text-sm font-semibold text-gray-800">
                          {{ `${result.firstName} ${result.lastName}` }}
                        </p>
                        <p class="text-xs text-gray-500">
                          {{ result.phoneNumber }}
                        </p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </transition>
          </div>

          <!-- Passenger -->
          <div class="relative w-full sm:w-56">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Passenger</label>
            <div class="relative mt-1.5">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span class="text-base text-gray-500 material-icons-round">search</span>
              </span>
              <input
                v-model="selectedPassenger"
                @keyup="searchPassenger"
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Search passenger..."
              />
            </div>
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div
                v-if="passengerSearchResults.length"
                class="absolute inset-x-0 z-50 mt-2"
              >
                <div
                  class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64"
                >
                  <ul>
                    <li
                      v-for="(result, index) in passengerSearchResults"
                      :key="result._id"
                      @click="selectPassenger(index)"
                      class="px-3 py-2 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                    >
                      <p class="text-sm font-semibold text-gray-800">
                        {{ `${result.firstName} ${result.lastName}` }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ result.phoneNumber }}
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </transition>
          </div>

          <!-- User -->
          <div class="relative w-full sm:w-56">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">User</label>
            <div class="relative mt-1.5">
              <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <span class="text-base text-gray-500 material-icons-round">search</span>
              </span>
              <input
                v-model="selectedUser"
                @keyup="searchUser"
                class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Search user..."
              />
            </div>
            <transition
              enter-active-class="transition duration-100 ease-out"
              enter-class="transform scale-95 opacity-0"
              enter-to-class="transform scale-100 opacity-100"
              leave-active-class="transition duration-75 ease-in"
              leave-class="transform scale-100 opacity-100"
              leave-to-class="transform scale-95 opacity-0"
            >
              <div
                v-if="userSearchResults.length"
                class="absolute inset-x-0 z-50 mt-2"
              >
                <div
                  class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64"
                >
                  <ul>
                    <li
                      v-for="(result, index) in userSearchResults"
                      :key="result._id"
                      @click="selectUser(index)"
                      class="px-3 py-2 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                    >
                      <p class="text-sm font-semibold text-gray-800">
                        {{ `${result.firstName} ${result.lastName}` }}
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            </transition>
          </div>

          <!-- Date range -->
          <div class="w-full sm:w-auto">
            <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Date Range</label>
            <div class="flex items-center h-10 mt-1.5 bg-white border border-gray-300 shadow-sm rounded-xl">
              <span class="pl-3 pr-1 text-base text-gray-500 material-icons-round">event</span>
              <v-date-picker v-model="filters.range" is-range>
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="flex items-center">
                    <input
                      :value="inputValue.start"
                      v-on="inputEvents.start"
                      placeholder="From"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                    <span class="mx-1 text-base text-gray-300 material-icons-round">arrow_forward</span>
                    <input
                      :value="inputValue.end"
                      v-on="inputEvents.end"
                      placeholder="To"
                      class="w-24 h-8 px-2 text-sm text-gray-800 bg-transparent rounded-lg focus:outline-none focus:bg-taxiye-50"
                    />
                  </div>
                </template>
              </v-date-picker>
              <button
                v-if="filters.range.start || filters.range.end"
                @click="filters.range = { start: null, end: null }"
                type="button"
                title="Clear dates"
                class="flex items-center justify-center w-8 h-8 mx-1 text-gray-400 rounded-lg transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none"
              >
                <span class="text-lg material-icons-round">close</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Export card -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header
          class="flex flex-wrap items-center justify-between gap-4 px-6 py-4 border-b border-gray-100"
        >
          <div class="flex items-start">
            <span
              class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
            >
              summarize
            </span>
            <div>
              <h3 class="text-base font-semibold text-gray-800">
                Batch Export
              </h3>
              <p class="text-sm text-gray-500">
                Choose modules to include in the export
              </p>
            </div>
          </div>
          <export-batch
            :working="loading"
            :app-modules="appModules"
            :filters="filters"
          ></export-batch>
        </header>
        <div class="overflow-hidden">
          <div class="overflow-x-auto">
            <app-modules v-model="appModules"></app-modules>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import ExportBatch from "@/components/ExportBatch";
import AppModules from "@/components/AppModules";

export default {
  components: {
    ExportBatch,
    AppModules,
  },
  data() {
    return {
      loading: true,

      selectedDriver: "",
      selectedPassenger: "",
      selectedUser: "",

      driverSearchResults: [],
      passengerSearchResults: [],
      userSearchResults: [],

      appModules: [],

      filters: {
        driverId: null,
        userId: null,
        passengerId: null,
        range: {
          start: null,
          end: null,
        },
      },
    };
  },
  created() {
    if (this.$route.query.passenger) {
      this.filters.passengerId = this.$route.query.passenger;
      this.$misc
        .get("users/" + this.$route.query.passenger)
        .then(({ data: { firstName, lastName, phoneNumber } }) => {
          this.selectedPassenger = `${firstName} ${lastName} - ${phoneNumber}`;
        });
    }
    if (this.$route.query.driver) {
      this.filters.driverId = this.$route.query.driver;
      this.$misc.get("drivers/" + this.$route.query.driver).then(({ data }) => {
        const { firstName, lastName, phoneNumber } = data.driver;
        this.selectedDriver = `${firstName} ${lastName} - ${phoneNumber}`;
      });
    }
    this.loading = false;
  },
  methods: {
    searchDriver(e) {
      if (e.target.value.trim() === "") {
        this.driverSearchResults = [];
        this.filters.driverId = null;
      } else {
        const name = e.target.value.trim();
        this.driverSearchResults = [];
        this.$misc
          .get("drivers/search?vehicle=true&limit=5&q=" + name)
          .then(({ data }) => {
            this.driverSearchResults = data;
          })
          .catch(err => console.log(err))
          .finally(() => (this.loading = false));
      }
    },

    searchUser(e) {
      if (e.target.value.trim() === "") {
        this.userSearchResults = [];
        this.filters.userId = null;
      } else {
        const name = e.target.value.trim();
        this.userSearchResults = [];
        this.$misc
          .get("accounts/search?vehicle=true&limit=5&q=" + name)
          .then(({ data }) => {
            this.userSearchResults = data;
          })
          .catch(err => console.log(err))
          .finally(() => (this.loading = false));
      }
    },

    searchPassenger(e) {
      if (e.target.value.trim() === "") {
        this.passengerSearchResults = [];
        this.filters.passengerId = null;
      } else {
        const name = e.target.value.trim();
        this.passengerSearchResults = [];
        this.$misc
          .get("users/search?vehicle=true&limit=5&q=" + name)
          .then(({ data }) => {
            this.passengerSearchResults = data;
          })
          .catch(err => console.log(err))
          .finally(() => (this.loading = false));
      }
    },

    selectDriver(index) {
      let driver = this.driverSearchResults[index];
      this.selectedDriver = `${driver.firstName} ${driver.lastName} - ${driver.phoneNumber}`;
      this.filters.driverId = driver._id;
      this.driverSearchResults = [];
      const query = { ...this.$route.query, driver: driver._id };
      this.$router.push({ query });
    },

    selectUser(index) {
      let user = this.userSearchResults[index];
      this.selectedUser = `${user.firstName} ${user.lastName}`;
      this.filters.userId = user._id;
      this.userSearchResults = [];
    },

    selectPassenger(index) {
      let user = this.passengerSearchResults[index];
      this.selectedPassenger = `${user.firstName} ${user.lastName} - ${user.phoneNumber}`;
      this.filters.passengerId = user._id;
      this.passengerSearchResults = [];
      const query = { ...this.$route.query, passenger: user._id };
      this.$router.push({ query });
    },
  },
};
</script>
